import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Plus,
  Search,
  Eye,
  AlertTriangle,
  RefreshCw,
  Trash2,
  CheckCircle,
  Info,
  Calendar,
  Package,
  Activity,
  MapPin,
  Shield
} from "lucide-react";

// ─── Blockchain access (NEW) ───
import { useBlockchainAccess } from "../../hooks/useBlockchainAccess";
import {
  BlockchainGate,
  BlockchainTierBadge,
  BlockchainUpsellCard,
  BlockchainStatusBanner,
} from "../../components/blockchain/BlockchainComponents";


// ============== TYPES ==============
type BatchStatus = "created" | "active" | "distributed" | "recalled" | "expired" | "archived";

interface Batch {
  _id?: string;
  id?: string;
  batchId: string;
  businessId: string;
  productId: string;
  assignedStoreId?: string | null;
  name?: string;
  batchNumber?: string;
  description?: string;
  quantity?: number;
  manufacturing?: {
    date?: string;
    location?: string;
    line?: string;
    shift?: string;
    supervisor?: string;
  };
  manufacturingDate?: string;
  expiryDate?: string | null;
  expiry?: {
    expiryDate?: string | null;
    isExpired?: boolean;
    gracePeriod?: number;
  };
  recall?: {
    isRecalled?: boolean;
    reason?: string | null;
    severity?: string | null;
    recallDate?: string;
  };
  stats?: {
    totalScans?: number;
    uniqueConsumers?: number;
    fraudAlerts?: number;
    firstScan?: string | null;
    lastScan?: string | null;
    totalTags?: number;
    activatedTags?: number;
    pendingTags?: number;
  };
  qualityControl?: {
    tested?: boolean;
  };
  blockchain?: {
    confirmed?: boolean;
  };
  status?: BatchStatus;
  statusHistory?: any[];
  createdAt?: string;
  updatedAt?: string;
}

interface CreateBatchForm {
  productId: string;
  name: string;
  description: string;
  quantity: number;
  manufacturing: {
    location: string;
    line: string;
    shift: string;
    supervisor: string;
  };
  // ─── Blockchain (NEW) ───
  // Optional URL whose content gets hashed and anchored on chain.
  // Only sent if the business has blockchain tier.
  traceUrl?: string;
}

type ApiResponse<T> = { 
  success: boolean; 
  data?: T; 
  message?: string; 
  error?: string; 
  meta?: any;
  batches?: Batch[];
};

// ============== API UTILITIES ==============
const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

function getAuthToken(): string | null {
  return localStorage.getItem("authToken") || 
         localStorage.getItem("businessToken") || 
         localStorage.getItem("token");
}

function getBusinessId(): string | null {
  try {
    const reg = localStorage.getItem("businessRegistration");
    if (reg) {
      const parsed = JSON.parse(reg);
      if (parsed?.businessId) return parsed.businessId;
    }
  } catch {}

  try {
    const user = localStorage.getItem("businessUser");
    if (user) {
      const parsed = JSON.parse(user);
      if (typeof parsed?.businessId === "string") return parsed.businessId;
      if (typeof parsed?.business?.businessId === "string") return parsed.business.businessId;
    }
  } catch {}

  return localStorage.getItem("currentBusinessId") || null;
}

async function apiFetch(path: string, init: RequestInit = {}) {
  const token = getAuthToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(init.headers as Record<string,string> || {}),
  };
  if (token) headers.Authorization = `Bearer ${token}`;
  
  const res = await fetch(`${API_BASE}/${path.replace(/^\//, "")}`, {
    ...init,
    headers,
  });
  
  let json: any = null;
  try { 
    json = await res.json(); 
  } catch {
    json = { success: false, error: "Invalid response format" };
  }
  
  if (!res.ok) {
    const msg = json?.message || json?.error || `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return json;
}

// ============== MAIN COMPONENT ==============
const BatchesPage: React.FC = () => {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState<string>("");
  const [creating, setCreating] = useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [selectedBatch, setSelectedBatch] = useState<Batch | null>(null);

  const [createForm, setCreateForm] = useState<CreateBatchForm>({
    productId: "",
    name: "",
    description: "",
    quantity: 100,
    manufacturing: {
      location: "",
      line: "",
      shift: "",
      supervisor: ""
    },
    traceUrl: ""
  });

  // ─── Blockchain access state (NEW) ───
  // Tells us whether to show the traceUrl field, upsell card, etc.
  const blockchain = useBlockchainAccess();

  const businessId = useMemo(() => getBusinessId(), []);

  const filtered = useMemo(() => {
    if (!search.trim()) return batches;
    const q = search.toLowerCase();
    return batches.filter((b) =>
      [b.batchId, b.name, b.batchNumber, b.productId, b.description]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [batches, search]);

  // ============== API FUNCTIONS ==============
  const loadBatches = useCallback(async () => {
    if (!businessId) {
      setError("Missing businessId. Please log in first.");
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response: ApiResponse<{ batches: Batch[] } | Batch[]> = await apiFetch('batches');
      
      let batchList: Batch[] = [];
      
      if (Array.isArray(response)) {
        batchList = response;
      } else if (response?.data) {
        if (Array.isArray(response.data)) {
          batchList = response.data;
        } else if ((response.data as any).batches && Array.isArray((response.data as any).batches)) {
          batchList = (response.data as any).batches;
        }
      } else if (response?.batches && Array.isArray(response.batches)) {
        batchList = response.batches;
      }
      
      setBatches(batchList);
      
    } catch (e: any) {
      setError(e?.message || "Failed to load batches");
      setBatches([]);
    } finally {
      setLoading(false);
    }
  }, [businessId]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!businessId) {
      setError("Missing businessId");
      return;
    }
    
    if (!createForm.productId.trim() || !createForm.name.trim()) {
      setError("Product ID and name are required");
      return;
    }

    setCreating(true);
    setError(null);
    
    try {
      const payload: any = {
        businessId,
        productId: createForm.productId.trim(),
        name: createForm.name.trim(),
        description: createForm.description.trim(),
        quantity: createForm.quantity,
        manufacturing: {
          location: createForm.manufacturing.location.trim(),
          line: createForm.manufacturing.line.trim(),
          shift: createForm.manufacturing.shift.trim(),
          supervisor: createForm.manufacturing.supervisor.trim()
        }
      };

      // ─── Include traceUrl ONLY if customer has blockchain access (NEW) ───
      if (blockchain.subscribed && createForm.traceUrl?.trim()) {
        payload.traceUrl = createForm.traceUrl.trim();
      }

      const response: ApiResponse<{ batch: Batch }> = await apiFetch('batches', {
        method: "POST",
        body: JSON.stringify(payload),
      });
      
      if (response.success && response.data?.batch) {
        setBatches(prev => [response.data!.batch, ...prev]);
        resetCreateForm();
        setShowCreateModal(false);
      } else {
        throw new Error(response.error || response.message || 'Failed to create batch');
      }
      
    } catch (e: any) {
      setError(e?.message || "Create failed");
    } finally {
      setCreating(false);
    }
  };

  const handleViewDetails = async (batch: Batch) => {
    try {
      const response: ApiResponse<{ batch: Batch }> = await apiFetch(
        `batches/${encodeURIComponent(batch.batchId)}`
      );
      
      if (response.success && response.data?.batch) {
        const updatedBatch = response.data.batch;
        setBatches(prev =>
          prev.map(b => (b.batchId === batch.batchId ? { ...b, ...updatedBatch } : b))
        );
        setSelectedBatch(updatedBatch);
      } else {
        setSelectedBatch(batch);
      }
      
    } catch (e: any) {
      setSelectedBatch(batch);
    }
  };

  const handleRecall = async (batch: Batch) => {
    const reason = prompt("Recall reason:", "Quality issue detected");
    const severity = prompt("Severity (low|medium|high|critical):", "high");
    
    if (!reason) return;
    
    try {
      const response: ApiResponse<{ batch: Batch }> = await apiFetch(
        `batches/${encodeURIComponent(batch.batchId)}/recall`, {
        method: "PATCH",
        body: JSON.stringify({ reason, severity: severity || "high" }),
      });
      
      if (response.success && response.data?.batch) {
        setBatches(prev => 
          prev.map(b => (b.batchId === batch.batchId ? { ...b, ...response.data!.batch } : b))
        );
      } else {
        throw new Error(response.error || response.message || 'Failed to recall batch');
      }
      
    } catch (e: any) {
      alert(`Recall failed: ${e?.message}`);
    }
  };

  const handleUnrecall = async (batch: Batch) => {
    const reason = prompt("Reason to revert recall:", "Issue resolved");
    
    if (!reason) return;
    
    try {
      const response: ApiResponse<{ batch: Batch }> = await apiFetch(
        `batches/${encodeURIComponent(batch.batchId)}/unrecall`, {
        method: "PATCH",
        body: JSON.stringify({ reason }),
      });
      
      if (response.success && response.data?.batch) {
        setBatches(prev => 
          prev.map(b => (b.batchId === batch.batchId ? { ...b, ...response.data!.batch } : b))
        );
      } else {
        throw new Error(response.error || response.message || 'Failed to un-recall batch');
      }
      
    } catch (e: any) {
      alert(`Un-recall failed: ${e?.message}`);
    }
  };

  const handleDelete = async (batch: Batch) => {
    if (!window.confirm(`Delete batch ${batch.batchId}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await apiFetch(
        `batches/${encodeURIComponent(batch.batchId)}`, {
        method: "DELETE",
      });
      
      setBatches(prev => prev.filter(b => b.batchId !== batch.batchId));
      
    } catch (e: any) {
      alert(`Delete failed: ${e?.message}`);
    }
  };

  const resetCreateForm = () => {
    setCreateForm({
      productId: "",
      name: "",
      description: "",
      quantity: 100,
      manufacturing: {
        location: "",
        line: "",
        shift: "",
        supervisor: ""
      },
      traceUrl: ""
    });
  };

  const getBatchStatusColor = (batch: Batch) => {
    if (batch.recall?.isRecalled || batch.status === "recalled") {
      return "bg-red-100 text-red-700 border-red-200";
    }
    switch (batch.status) {
      case "created": return "bg-gray-100 text-gray-700 border-gray-200";
      case "active": return "bg-blue-100 text-blue-700 border-blue-200";
      case "distributed": return "bg-green-100 text-green-700 border-green-200";
      case "expired": return "bg-orange-100 text-orange-700 border-orange-200";
      case "archived": return "bg-purple-100 text-purple-700 border-purple-200";
      default: return "bg-gray-100 text-gray-700 border-gray-200";
    }
  };

  const formatDate = (dateString?: string | null) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString?: string | null) => {
    if (!dateString) return "Not set";
    return new Date(dateString).toLocaleString();
  };

  useEffect(() => {
    loadBatches();
  }, [loadBatches]);

  // ============== RENDER ==============
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Batch Management</h1>
            <p className="text-gray-600 flex items-center gap-2 mt-1">
              <Info className="w-4 h-4" />
              Manage production batches and their lifecycle
            </p>
            {businessId && (
              <p className="text-sm text-gray-500 mt-1">
                Business ID: <span className="font-mono">{businessId}</span>
              </p>
            )}
            {/* ─── Blockchain tier badge (NEW) ─── */}
            {blockchain.subscribed && (
              <div className="mt-2">
                <BlockchainTierBadge />
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadBatches}
              disabled={loading}
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Batch
            </button>
          </div>
        </div>

        {/* ─── Blockchain status banner (NEW) ─── */}
        {/* Shows warnings like "feature paused" or "monthly limit reached" */}
        <BlockchainStatusBanner />

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg px-4 py-3">
            <div className="flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2" />
              {error}
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg p-4 shadow-sm border">
            <div className="text-2xl font-bold text-blue-600">{batches.length}</div>
            <div className="text-sm text-gray-600">Total Batches</div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border">
            <div className="text-2xl font-bold text-green-600">
              {batches.filter(b => b.status === 'active').length}
            </div>
            <div className="text-sm text-gray-600">Active</div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border">
            <div className="text-2xl font-bold text-red-600">
              {batches.filter(b => b.recall?.isRecalled).length}
            </div>
            <div className="text-sm text-gray-600">Recalled</div>
          </div>
          <div className="bg-white rounded-lg p-4 shadow-sm border">
            <div className="text-2xl font-bold text-purple-600">
              {batches.reduce((sum, b) => sum + (b.quantity || 0), 0)}
            </div>
            <div className="text-sm text-gray-600">Total Units</div>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white border rounded-xl p-4 shadow-sm">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by Batch ID, Name, Product ID, or Description"
              className="w-full border rounded-lg pl-9 pr-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>

        {/* Batches Table */}
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
                <p className="mt-3 text-gray-500">Loading batches...</p>
              </div>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">
                {search ? "No matching batches" : "No batches yet"}
              </h3>
              <p className="text-gray-500 text-sm">
                {search ? "Try a different search term." : "Create your first batch to get started."}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Batch</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Product ID</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Qty</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Tags</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Dates</th>
                    <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filtered.map((batch) => (
                    <tr key={batch.batchId || batch._id} className="hover:bg-gray-50 transition-colors">
                      {/* Batch Info */}
                      <td className="px-6 py-4">
                        <div className="text-sm font-semibold text-gray-900">{batch.name || batch.batchNumber || "Unnamed"}</div>
                        <div className="text-xs text-gray-400 font-mono mt-0.5">{batch.batchId}</div>
                        {batch.description && (
                          <div className="text-xs text-gray-400 mt-0.5 truncate max-w-[200px]">{batch.description}</div>
                        )}
                      </td>

                      {/* Product */}
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-700 font-mono">{batch.productId || "—"}</div>
                      </td>

                      {/* Status */}
                      <td className="px-6 py-4">
                        <span className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full border ${getBatchStatusColor(batch)}`}>
                          {batch.recall?.isRecalled ? "RECALLED" : (batch.status || "unknown")}
                        </span>
                      </td>

                      {/* Quantity */}
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {(batch.quantity || 0).toLocaleString()}
                      </td>

                      {/* Tags */}
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900">
                          {batch.stats?.activatedTags || 0} / {batch.stats?.totalTags || 0}
                        </div>
                        <div className="text-xs text-gray-400">activated</div>
                      </td>

                      {/* Dates */}
                      <td className="px-6 py-4">
                        <div className="text-xs text-gray-600">
                          <div>Mfg: {formatDate(batch.manufacturing?.date || batch.manufacturingDate)}</div>
                          <div>Exp: {formatDate(batch.expiryDate || batch.expiry?.expiryDate)}</div>
                        </div>
                      </td>

                      {/* Actions */}
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleViewDetails(batch)}
                            className="p-2 rounded-lg hover:bg-blue-50 text-blue-600 transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {batch.recall?.isRecalled ? (
                            <button
                              onClick={() => handleUnrecall(batch)}
                              className="p-2 rounded-lg hover:bg-green-50 text-green-600 transition-colors"
                              title="Un-recall"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          ) : (
                            <button
                              onClick={() => handleRecall(batch)}
                              className="p-2 rounded-lg hover:bg-yellow-50 text-yellow-600 transition-colors"
                              title="Recall Batch"
                            >
                              <AlertTriangle className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(batch)}
                            className="p-2 rounded-lg hover:bg-red-50 text-red-500 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ═══ Create Batch Modal ═══ */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Create New Batch</h3>
              <button onClick={() => setShowCreateModal(false)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400">✕</button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Batch Name *</label>
                <input
                  value={createForm.name}
                  onChange={(e) => setCreateForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g. Summer Edition 2026"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Product ID *</label>
                <input
                  value={createForm.productId}
                  onChange={(e) => setCreateForm(f => ({ ...f, productId: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                  placeholder="PROD-XXXXXXXXXXXX"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={createForm.description}
                  onChange={(e) => setCreateForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  rows={2}
                  placeholder="Optional batch description..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                  <input
                    type="number"
                    min={1}
                    value={createForm.quantity}
                    onChange={(e) => setCreateForm(f => ({ ...f, quantity: parseInt(e.target.value) || 1 }))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                  <input
                    value={createForm.manufacturing.location}
                    onChange={(e) => setCreateForm(f => ({ ...f, manufacturing: { ...f.manufacturing, location: e.target.value } }))}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Factory location"
                  />
                </div>
              </div>

              {/* ═══════════════════════════════════════════════════════
                  ⛓ Blockchain section (NEW)
                  - Premium customers see a traceUrl input field
                  - Non-premium customers see an upsell card
                  - Customers who subscribed but disabled it: traceUrl is hidden
                  ═══════════════════════════════════════════════════════ */}
              <BlockchainGate
                requireAllowed={true}
                fallback={
                  blockchain.subscribed ? null : (
                    <BlockchainUpsellCard
                      title="Add tamper-proof traceability"
                      description="Premium customers can attach a permanent blockchain proof to each batch. Customers can independently verify your product's authenticity."
                      ctaText="Learn about Premium"
                    />
                  )
                }
              >
                <div className="border-t pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-purple-600" />
                    Traceability URL
                    <span className="text-xs font-normal text-gray-500">(optional, blockchain anchor)</span>
                  </label>
                  <input
                    type="url"
                    value={createForm.traceUrl || ""}
                    onChange={(e) => setCreateForm(f => ({ ...f, traceUrl: e.target.value }))}
                    placeholder="https://yoursite.com/products/abc123"
                    className="w-full px-4 py-2.5 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    On activation, we'll fetch this URL and anchor a hash on the blockchain. Leave empty to skip.
                  </p>
                </div>
              </BlockchainGate>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); resetCreateForm(); }}
                  className="px-5 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={creating}
                  className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors inline-flex items-center gap-2"
                >
                  {creating ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Creating...</>
                  ) : (
                    <><Plus className="w-4 h-4" /> Create Batch</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ═══ Batch Detail Modal ═══ */}
      {selectedBatch && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedBatch(null)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900">{selectedBatch.name || selectedBatch.batchNumber || "Batch Details"}</h3>
                <p className="text-xs text-gray-400 font-mono mt-0.5">{selectedBatch.batchId}</p>
              </div>
              <button onClick={() => setSelectedBatch(null)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400">✕</button>
            </div>
            <div className="p-6 space-y-5">
              {/* Status */}
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 text-sm font-semibold rounded-full border ${getBatchStatusColor(selectedBatch)}`}>
                  {selectedBatch.recall?.isRecalled ? "RECALLED" : (selectedBatch.status || "unknown")}
                </span>
                {selectedBatch.recall?.isRecalled && selectedBatch.recall.reason && (
                  <span className="text-sm text-red-600">— {selectedBatch.recall.reason}</span>
                )}
              </div>

              {/* Info Grid */}
              <div className="grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
                <div>
                  <span className="font-medium text-gray-500">Product ID</span>
                  <p className="text-gray-900 font-mono mt-0.5">{selectedBatch.productId || "—"}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-500">Quantity</span>
                  <p className="text-gray-900 mt-0.5">{(selectedBatch.quantity || 0).toLocaleString()} units</p>
                </div>
                <div>
                  <span className="font-medium text-gray-500">Manufacturing</span>
                  <p className="text-gray-900 mt-0.5">{formatDate(selectedBatch.manufacturing?.date || selectedBatch.manufacturingDate)}</p>
                  {selectedBatch.manufacturing?.location && (
                    <p className="text-xs text-gray-400 flex items-center gap-1 mt-0.5">
                      <MapPin className="w-3 h-3" />{selectedBatch.manufacturing.location}
                    </p>
                  )}
                </div>
                <div>
                  <span className="font-medium text-gray-500">Expiry</span>
                  <p className="text-gray-900 mt-0.5">{formatDate(selectedBatch.expiryDate || selectedBatch.expiry?.expiryDate)}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-500">Tags</span>
                  <p className="text-gray-900 mt-0.5">
                    <span className="text-green-600 font-semibold">{selectedBatch.stats?.activatedTags || 0}</span>
                    {" / "}
                    <span>{selectedBatch.stats?.totalTags || 0}</span>
                    <span className="text-gray-400 ml-1">activated</span>
                  </p>
                </div>
                <div>
                  <span className="font-medium text-gray-500">Store</span>
                  <p className="text-gray-900 mt-0.5 font-mono text-xs">{selectedBatch.assignedStoreId || "Unassigned"}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-500">Created</span>
                  <p className="text-gray-900 mt-0.5">{formatDateTime(selectedBatch.createdAt)}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-500">Updated</span>
                  <p className="text-gray-900 mt-0.5">{formatDateTime(selectedBatch.updatedAt)}</p>
                </div>
              </div>

              {selectedBatch.description && (
                <div>
                  <span className="text-sm font-medium text-gray-500">Description</span>
                  <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg mt-1">{selectedBatch.description}</p>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="flex gap-2">
                  {selectedBatch.recall?.isRecalled ? (
                    <button
                      onClick={() => { handleUnrecall(selectedBatch); setSelectedBatch(null); }}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm inline-flex items-center gap-2"
                    >
                      <CheckCircle className="w-4 h-4" /> Un-recall
                    </button>
                  ) : (
                    <button
                      onClick={() => { handleRecall(selectedBatch); setSelectedBatch(null); }}
                      className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors text-sm inline-flex items-center gap-2"
                    >
                      <AlertTriangle className="w-4 h-4" /> Recall
                    </button>
                  )}
                  <button
                    onClick={() => { handleDelete(selectedBatch); setSelectedBatch(null); }}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm inline-flex items-center gap-2"
                  >
                    <Trash2 className="w-4 h-4" /> Delete
                  </button>
                </div>
                <button
                  onClick={() => setSelectedBatch(null)}
                  className="px-5 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BatchesPage;